import { useCallback, useContext, useSyncExternalStore } from 'react';
import type {
  ActieArgumenten,
  ActieNaam,
  ActieResultaat,
  Game,
  Players,
  SpelStaat,
} from '@ame/shared';
import { SpelContext } from './context';
import type { SpelMomentopname, SpelVerbinding } from './verbinding';

function useVerbinding(): SpelVerbinding {
  const verbinding = useContext(SpelContext);
  if (!verbinding) throw new Error('Deze hook werkt alleen binnen een <SpelProvider>.');
  return verbinding;
}

/**
 * De staat en de verbindingsstatus van het spel. Rendert opnieuw bij elke nieuwe staat.
 *
 * Let op: elke `staat` van de server is een geheel nieuw object, ook de onderdelen die
 * niet veranderden. Gebruik in de afhankelijkheden van `useEffect` of `useMemo` dus liever
 * een getal of tekst (`game.round`, `effectQueue[0]`) dan een object of lijst uit de staat.
 */
export function useSpel(): SpelMomentopname {
  const verbinding = useVerbinding();
  return useSyncExternalStore(verbinding.subscribe, verbinding.getSnapshot);
}

/**
 * Een functie die de staat van dit moment geeft, voor code die na een `await` verder rekent.
 * `useSpel().staat` is die van de laatste render en kan dan al achterlopen. Na een gelukte
 * actie bevat hij de wijziging al: de server stuurt de nieuwe staat vóór het resultaat.
 */
export function useLeesStaat(): () => SpelStaat | null {
  const verbinding = useVerbinding();
  return useCallback(() => verbinding.getSnapshot().staat, [verbinding]);
}

/** Het spel-document (`GamesCollection` in het origineel), of `undefined` zolang het er niet is. */
export function useGame(): Game | undefined {
  return useSpel().staat?.game;
}

/** Het spelers-document (`PlayersCollection` in het origineel), of `undefined`. */
export function usePlayers(): Players | undefined {
  return useSpel().staat?.players;
}

/** Het spel-id van de omringende `SpelProvider`. */
export function useSpelId(): string {
  return useVerbinding().spelId;
}

/**
 * Voert een actie uit: `actie('setPlayerName', playerId, username)`. Getypeerd met het
 * actieregister van `@ame/shared`, dus een onbekende naam of verkeerde argumenten is een
 * typefout en de belofte geeft het juiste type terug.
 */
export type ActieFunctie = <N extends ActieNaam>(
  naam: N,
  ...args: ActieArgumenten<N>
) => Promise<ActieResultaat<N>>;

/**
 * Een functie om acties uit te voeren, de vervanging van `Meteor.call`. De naam en de
 * argumenten zijn die van het origineel zonder het spel-id. De belofte geeft de waarde
 * van de actie, of wijst af met een `ActieFout` (zie `verbinding.ts`).
 *
 * De functie blijft dezelfde zolang het spel hetzelfde is.
 */
export function useActie(): ActieFunctie {
  const verbinding = useVerbinding();
  return useCallback<ActieFunctie>(
    // De server geeft de waarde van de actie terug; het type komt uit het register.
    (naam, ...args) => verbinding.actie(naam, args) as Promise<never>,
    [verbinding],
  );
}
